import React, { FormEvent, useState } from "react";
import { useDispatch } from "react-redux";
import * as ajaxEndpoints from "../../../../api/ajax-endpoints";
import { hideModal } from "../../../../redux/actions/modal";
import ButtonLoader from "../../../../UI/Loaders/Loaders";
import { appInsights } from "../../../../config/appInsights";
import {
  handleSwalErrorAlert,
  handleSwalSuccessAlert,
} from "../../../../helpers";
import { postData } from "../../../../newApis/dotNetApiMethods";
import { useVerifyIdentity } from "../../../../custom-hooks/useVerifyIdentity";
import { useBusinessTypes } from "../../../../custom-hooks/useBusinessTypes";
import { useIdentityVerificationTypes } from "../../../../components/DigitalBank/BackOfficeBusinessOnboarding/hooks/useIdentityVerificationTypes";
import { message } from "antd";

interface CreateBlackListModalProps {
  dataRecord?: {
    refetch?: () => void;
  };
  onCloseModal: () => void;
}

const CreateBlackListModal: React.FC<CreateBlackListModalProps> = ({
  dataRecord,
  onCloseModal,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [verifyingBvn, setVerifyingBvn] = useState(false);
  const [bvnVerified, setBvnVerified] = useState(false);
  const [verifyingNin, setVerifyingNin] = useState(false);
  const [ninVerified, setNinVerified] = useState(false);
  const [verifyingRc, setVerifyingRc] = useState(false);
  const [rcVerified, setRcVerified] = useState(false);
  const [state, setState] = useState({
    customerType: "Individual",
    firstName: "",
    lastName: "",
    businessName: "",
    bvn: "",
    nin: "",
    rcNumber: "",
    businessType: "",
    email: "",
    phoneNumber: "",
    activationReason: "",
  });

  const { data: businessTypesData, isLoading: isLoadingBusinessTypes } =
    useBusinessTypes();
  const businessTypes = businessTypesData?.data || [];

  const { data: identityTypesData } = useIdentityVerificationTypes();
  const identityTypes = identityTypesData?.data || [];

  const getBusinessTypeName = (businessTypeId: string) => {
    const type = businessTypes.find((bt) => bt.id === parseInt(businessTypeId));
    return type?.id;
  };

  const getIdentityVerificationType = (typeName: string) => {
    const type = identityTypes.find(
      (it) => it.name.toLowerCase() === typeName.toLowerCase()
    );
    return type?.name || typeName;
  };

  const { mutate: verifyIdentity } = useVerifyIdentity();

  const handleCustomerTypeChange = (newCustomerType: string) => {
    // Clear all verification states and form fields when customer type changes
    setBvnVerified(false);
    setNinVerified(false);
    setRcVerified(false);
    setState((prev) => ({
      ...prev,
      customerType: newCustomerType,
      firstName: "",
      lastName: "",
      businessName: "",
      bvn: "",
      nin: "",
      rcNumber: "",
      businessType: "",
    }));
  };

  const handleBvnBlur = async () => {
    if (!state.bvn || state.bvn.length !== 11) {
      setBvnVerified(false);
      return;
    }

    setVerifyingBvn(true);
    verifyIdentity(
      {
        identityNumber: state.bvn,
        identityVerificationType: getIdentityVerificationType("Bvn"),
        customerType: state.customerType,
        ...(state.customerType === "Organisation" && state.businessType
          ? { businessType: getBusinessTypeName(state.businessType) }
          : {}),
      },
      {
        onSuccess: (result) => {
          setBvnVerified(true);
          message.success("BVN verified successfully");

          console.log("BVN Verification Result:", result);

          if (state.customerType === "Individual") {
            setState((prev) => ({
              ...prev,
              firstName: result.data.firstName!,
              lastName: result.data.firstName!,
            }));
          }
          setVerifyingBvn(false);
        },
        onError: () => {
          setBvnVerified(false);
          setVerifyingBvn(false);
        },
      }
    );
  };

  const handleRcNumberBlur = async () => {
    if (!state.rcNumber) {
      setRcVerified(false);
      return;
    }

    if (!state.businessType) {
      message.error("Please select business type first");
      return;
    }

    setVerifyingRc(true);
    verifyIdentity(
      {
        identityNumber: state.rcNumber,
        identityVerificationType: getIdentityVerificationType("RcNumber"),
        customerType: state.customerType,
        businessType: getBusinessTypeName(state.businessType),
      },
      {
        onSuccess: (result) => {
          setRcVerified(true);
          message.success("RC Number verified successfully");

          // Auto-fill business name from verification result
          setState((prev) => ({
            ...prev,
            businessName: result.data.businessName!,
          }));
          setVerifyingRc(false);
        },
        onError: () => {
          setRcVerified(false);
          setVerifyingRc(false);
        },
      }
    );
  };

  const handleNinBlur = async () => {
    if (!state.nin || state.nin.length !== 11) {
      setNinVerified(false);
      return;
    }

    setVerifyingNin(true);
    verifyIdentity(
      {
        identityNumber: state.nin,
        identityVerificationType: getIdentityVerificationType("Nin"),
        customerType: state.customerType,
      },
      {
        onSuccess: (result) => {
          setNinVerified(true);
          message.success("NIN verified successfully");

          // Auto-fill first and last name for Individual customers
          // if (state.customerType === "Individual") {
          //   setState((prev) => ({
          //     ...prev,
          //     firstName: result.data.firstName!,
          //     lastName: result.data.lastName!,
          //   }));
          // }
          setVerifyingNin(false);
        },
        onError: () => {
          setNinVerified(false);
          setVerifyingNin(false);
        },
      }
    );
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Validation: Check if BVN is verified when provided
    if (state.bvn && !bvnVerified) {
      message.error("Please verify the BVN before submitting");
      return;
    }

    // Validation: Check if NIN is verified when provided
    if (state.nin && !ninVerified) {
      message.error("Please verify the NIN before submitting");
      return;
    }

    // Validation: Check if RC Number is verified for Organisation customers
    if (
      state.customerType === "Organisation" &&
      state.rcNumber &&
      !rcVerified
    ) {
      message.error("Please verify the RC Number before submitting");
      return;
    }

    const payload = {
      customerType: state.customerType,
      firstName: state.customerType === "Individual" ? state.firstName : null,
      lastName: state.customerType === "Individual" ? state.lastName : null,
      businessName:
        state.customerType === "Organisation" ? state.businessName : null,
      bvn: state.bvn,
      nin: state.customerType === "Individual" ? state.nin : null,
      rcNumber: state.customerType === "Organisation" ? state.rcNumber : null,
      businessType:
        state.customerType === "Organisation" && state.businessType
          ? parseInt(state.businessType)
          : null,
      email: state.email,
      phoneNumber: state.phoneNumber,
      activationReason: state.activationReason,
    };

    setLoading(true);

    try {
      const response = await postData(ajaxEndpoints.DB_BLACKLIST, payload);

      setLoading(false);
      handleSwalSuccessAlert(
        response?.data?.message || "BlackList entry created successfully",
        () => {
          dispatch(hideModal());
          // Refetch the blacklist data
          if (dataRecord?.refetch) {
            dataRecord.refetch();
          }
        }
      );
    } catch (error: any) {
      setLoading(false);
      appInsights.trackException({
        exception: error,
        properties: {
          fileName: "CreateBlackListModal.tsx",
        },
      });

      handleSwalErrorAlert(
        error?.response?.data?.message ||
          error?.message ||
          "Network error. Please try again."
      );
    }
  };

  return (
    <div
      className="Modal__dialog lg-modal animated slideInDown"
      role="document"
    >
      <div className="LoanModal Modal__content">
        <div className="Modal__body" style={{ overflow: "hidden" }}>
          <React.Fragment>
            <h3 className="Modal__title p-b-1">Create BlackList Entry</h3>

            {/* Create BlackList Form */}
            <div id="blacklist-confirm">
              <div className="m-b-30">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 col-sm-12 mt-2">
                      <label htmlFor="customerType">
                        Customer Type <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control"
                        id="customerType"
                        name="customerType"
                        value={state.customerType}
                        onChange={(e) =>
                          handleCustomerTypeChange(e.target.value)
                        }
                        required
                      >
                        <option value="Individual">Individual Customer</option>
                        <option value="Organisation">Business Customer</option>
                      </select>
                    </div>

                    {state.customerType === "Individual" ? (
                      <>
                        <div className="col-md-6 col-sm-12 mt-2">
                          <label htmlFor="firstName">
                            First Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="firstName"
                            name="firstName"
                            value={state.firstName}
                            onChange={(e) => {
                              setState((prev) => ({
                                ...prev,
                                firstName: e.target.value,
                              }));
                            }}
                            placeholder="Enter first name"
                            required
                            disabled={bvnVerified || ninVerified}
                            title={
                              bvnVerified || ninVerified
                                ? "Field auto-filled from verification"
                                : ""
                            }
                          />
                        </div>

                        <div className="col-md-6 col-sm-12 mt-2">
                          <label htmlFor="lastName">
                            Last Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="lastName"
                            name="lastName"
                            value={state.lastName}
                            onChange={(e) => {
                              setState((prev) => ({
                                ...prev,
                                lastName: e.target.value,
                              }));
                            }}
                            placeholder="Enter last name"
                            required
                            disabled={bvnVerified || ninVerified}
                            title={
                              bvnVerified || ninVerified
                                ? "Field auto-filled from verification"
                                : ""
                            }
                          />
                        </div>

                        <div className="col-md-6 col-sm-12 mt-2">
                          <label htmlFor="bvn">
                            BVN <span className="text-danger">*</span>
                            {verifyingBvn && (
                              <span className="ml-2 text-info">
                                <i className="fa fa-spinner fa-spin" />{" "}
                                Verifying...
                              </span>
                            )}
                            {bvnVerified && !verifyingBvn && (
                              <span className="ml-2 text-success">
                                <i className="fa fa-check-circle" /> Verified
                              </span>
                            )}
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="bvn"
                            name="bvn"
                            value={state.bvn}
                            onChange={(e) => {
                              setBvnVerified(false);
                              setState((prev) => ({
                                ...prev,
                                bvn: e.target.value,
                              }));
                            }}
                            onBlur={handleBvnBlur}
                            placeholder="Enter BVN (11 digits)"
                            maxLength={11}
                            required
                          />
                        </div>

                        <div className="col-md-6 col-sm-12 mt-2">
                          <label htmlFor="nin">
                            NIN
                            {verifyingNin && (
                              <span className="ml-2 text-info">
                                <i className="fa fa-spinner fa-spin" />{" "}
                                Verifying...
                              </span>
                            )}
                            {ninVerified && !verifyingNin && (
                              <span className="ml-2 text-success">
                                <i className="fa fa-check-circle" /> Verified
                              </span>
                            )}
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="nin"
                            name="nin"
                            value={state.nin}
                            onChange={(e) => {
                              setNinVerified(false);
                              setState((prev) => ({
                                ...prev,
                                nin: e.target.value,
                              }));
                            }}
                            onBlur={handleNinBlur}
                            placeholder="Enter NIN (11 digits)"
                            maxLength={11}
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="col-md-6 col-sm-12 mt-2">
                          <label htmlFor="businessType">
                            Business Type <span className="text-danger">*</span>
                          </label>
                          <select
                            className="form-control"
                            id="businessType"
                            name="businessType"
                            value={state.businessType}
                            onChange={(e) => {
                              setRcVerified(false); // Reset RC verification when business type changes
                              setState((prev) => ({
                                ...prev,
                                businessType: e.target.value,
                              }));
                            }}
                            required
                            disabled={isLoadingBusinessTypes}
                          >
                            <option value="">
                              {isLoadingBusinessTypes
                                ? "Loading..."
                                : "Select business type"}
                            </option>
                            {businessTypes.map((type) => (
                              <option key={type.id} value={type.id}>
                                {type.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-md-6 col-sm-12 mt-2">
                          <label htmlFor="rcNumber">
                            RC Number <span className="text-danger">*</span>
                            {verifyingRc && (
                              <span className="ml-2 text-info">
                                <i className="fa fa-spinner fa-spin" />{" "}
                                Verifying...
                              </span>
                            )}
                            {rcVerified && !verifyingRc && (
                              <span className="ml-2 text-success">
                                <i className="fa fa-check-circle" /> Verified
                              </span>
                            )}
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="rcNumber"
                            name="rcNumber"
                            value={state.rcNumber}
                            onChange={(e) => {
                              setRcVerified(false);
                              setState((prev) => ({
                                ...prev,
                                rcNumber: e.target.value,
                              }));
                            }}
                            onBlur={handleRcNumberBlur}
                            placeholder="Enter RC number"
                            required
                            disabled={!state.businessType}
                            title={
                              !state.businessType
                                ? "Please select business type first"
                                : ""
                            }
                          />
                        </div>

                        <div className="col-md-6 col-sm-12 mt-2">
                          <label htmlFor="businessName">
                            Business Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="businessName"
                            name="businessName"
                            value={state.businessName}
                            onChange={(e) => {
                              setState((prev) => ({
                                ...prev,
                                businessName: e.target.value,
                              }));
                            }}
                            placeholder="Enter business name"
                            required
                            disabled={rcVerified}
                            title={
                              rcVerified
                                ? "Field auto-filled from RC Number verification"
                                : ""
                            }
                          />
                        </div>

                        <div className="col-md-6 col-sm-12 mt-2">
                          <label htmlFor="bvn">
                            BVN
                            {verifyingBvn && (
                              <span className="ml-2 text-info">
                                <i className="fa fa-spinner fa-spin" />{" "}
                                Verifying...
                              </span>
                            )}
                            {bvnVerified && !verifyingBvn && (
                              <span className="ml-2 text-success">
                                <i className="fa fa-check-circle" /> Verified
                              </span>
                            )}
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="bvn"
                            name="bvn"
                            value={state.bvn}
                            onChange={(e) => {
                              setBvnVerified(false);
                              setState((prev) => ({
                                ...prev,
                                bvn: e.target.value,
                              }));
                            }}
                            onBlur={handleBvnBlur}
                            placeholder="Enter BVN (11 digits)"
                            maxLength={11}
                          />
                        </div>
                      </>
                    )}

                    <div className="col-md-6 col-sm-12 mt-2">
                      <label htmlFor="email">
                        Email <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        value={state.email}
                        onChange={(e) => {
                          setState((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }));
                        }}
                        placeholder="Enter email address"
                        required
                      />
                    </div>

                    <div className="col-md-6 col-sm-12 mt-2">
                      <label htmlFor="phoneNumber">
                        Phone Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={state.phoneNumber}
                        onChange={(e) => {
                          setState((prev) => ({
                            ...prev,
                            phoneNumber: e.target.value,
                          }));
                        }}
                        placeholder="Enter phone number"
                        required
                      />
                    </div>

                    <div className="col-md-12 col-sm-12 mt-2">
                      <label htmlFor="activationReason">
                        Reason for Blacklisting{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        id="activationReason"
                        name="activationReason"
                        value={state.activationReason}
                        onChange={(e) => {
                          setState((prev) => ({
                            ...prev,
                            activationReason: e.target.value,
                          }));
                        }}
                        placeholder="Enter reason for blacklisting this customer"
                        rows={4}
                        required
                      />
                    </div>
                  </div>

                  <div className="Form__action-buttons m-t-2">
                    <button
                      type="button"
                      onClick={onCloseModal}
                      className="btn zoom-element btn login-btn m-0 mr-auto"
                      disabled={loading}
                    >
                      <i className="fa fa-times-circle" /> Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn zoom-element btn login-btn m-0 ml-auto"
                      disabled={loading}
                    >
                      <i className="fa fa-check-circle" /> Create BlackList
                      Entry
                      {loading && <ButtonLoader />}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </React.Fragment>
        </div>
      </div>
    </div>
  );
};

export default CreateBlackListModal;
